import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// mui import
import { Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import "react-pro-sidebar/dist/css/styles.css";

// project import
import { tokens } from "../../theme";
import { existRegulationWithNoCitation } from "../../utilities/helpers";
import assignmentApi from "../../apis/assignmentApi";
import Item from "./item";
import General from "./general";
import Pai from "./pai";
import Practice from "./SideBar/practice";
import EqipSideBar from "../../components/EqipSideBar";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";
import { COMMODITY_TENANT_LIST, MARKET, SUBMARKET } from "../../utilities/constants";
import Practice503b from "./SideBar/practice503b";
import Practice503a from "./SideBar/practice503a";
import useLookupData from "../../hooks/useLookupData";

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState("Dashboard");

  const [assignment, setAssignment] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [subMarkets, setSubMarkets] = useState([]);
  const [lookups, setLookups] = useState(null);
  const [b503Operations, setb503Operations] = useState([]);
  const [tempDummyOperation, setTempDummyOperation] = useState(null);
  const [a503Operations, seta503Operations] = useState([]);
  const [tempDummy503aOperation, setTempDummy503aOperation] = useState(null);

  // Get current location to check if URL contains "subPartCode"
  const location = useLocation();
  const hasSubPartCodeInUrl = location.pathname.includes("subPartCode");
  
  // Use custom hook for lookup data
  const { lookup } = useLookupData();
  
  // const isShowInspection = Boolean(useSelector((state) => {
  //   return state.isShowInspection;
  // })); 

  const tenantId = useSelector((state) => {
    return state.tenantId;
  });

  const inspectionUid = useSelector((state) => {
    return state.inspectionUid;
  });

  const assignmentUid = useSelector((state) => {
    return state.assignmentUid;
  });

  const idToken = useSelector((state) => {
    return state.token;
  });


  let subPart = ""; // hold subpart indicator to show its sections
  let subPartCode = "";
  // Q7
  let subPartQ7 = ""; // hold subpart indicator to show its sections
  let subPartCodeQ7 = "";

  // Cfr111
  let subPartCfr111 = ""; // hold subpart indicator to show its sections
  let subPartCodeCfr111 = "";
  
  // Cfr820
  let subPartCfr820 = ""; // hold subpart indicator to show its sections
  let subPartCodeCfr820 = "";

  // Cfr820211
  let subPartCfr820211 = ""; // hold subpart indicator to show its sections
  let subPartCodeCfr820211 = "";

  // Cfr211820
  let subPartCfr211820 = ""; // hold subpart indicator to show its sections
  let subPartCodeCfr211820 = "";

  // Listen for 503B dummy item creation and real operation creation
  useEffect(() => {
    const handleDummyItemCreated = (event) => {
      console.log("503B dummy item created, adding to sidebar...", event.detail);
      setTempDummyOperation(event.detail.dummyOperation);
    };

    const handle503BCreated = (event) => {
      console.log("503B operation created, removing dummy and refreshing...", event.detail);
      // Remove dummy item and refresh real operations
      setTempDummyOperation(null);
      // Refresh the operations list to include the new real operation
      const refreshOperations = async () => {
        try {
          let response503bOperations;
          try {
            response503bOperations = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503boperations`);
          } catch (primaryError) {
            response503bOperations = await ieqipCoreServiceApi.get(`/api/inspections/list/${inspectionUid}/inspection503boperations`);
          }
          const operations = response503bOperations.data?.body || response503bOperations.data || [];
          setb503Operations(operations);
        } catch (error) {
          console.warn("Failed to refresh 503B operations:", error.message);
        }
      };
      refreshOperations();
    };

    const handle503BUpdated = (event) => {
      console.log("503B operation updated, refreshing...", event.detail);
      // Refresh the operations list to show updated data
      const refreshOperations = async () => {
        try {
          let response503bOperations;
          try {
            response503bOperations = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503boperations`);
          } catch (primaryError) {
            response503bOperations = await ieqipCoreServiceApi.get(`/api/inspections/list/${inspectionUid}/inspection503boperations`);
          }
          const operations = response503bOperations.data?.body || response503bOperations.data || [];
          setb503Operations(operations);
        } catch (error) {
          console.warn("Failed to refresh 503B operations:", error.message);
        }
      };
      refreshOperations();
    };

    const handle503BDeleted = (event) => {
      const { deletedOperationUid } = event.detail;
      setb503Operations(prev => prev.filter(op => op.inspection_503b_operation_uid !== deletedOperationUid));
    };

    window.addEventListener('503bDummyItemCreated', handleDummyItemCreated);
    window.addEventListener('503bOperationCreated', handle503BCreated);
    window.addEventListener('503bOperationUpdated', handle503BUpdated);
    window.addEventListener('503bOperationDeleted', handle503BDeleted);

    return () => {
      window.removeEventListener('503bDummyItemCreated', handleDummyItemCreated);
      window.removeEventListener('503bOperationCreated', handle503BCreated);
      window.removeEventListener('503bOperationUpdated', handle503BUpdated);
      window.removeEventListener('503bOperationDeleted', handle503BDeleted);
    };
  }, [inspectionUid]);

  // Listen for 503A dummy item creation and real operation creation
  useEffect(() => {
    const refresh503aOperations = async () => {
      try {
        const response = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503aoperations`);
        seta503Operations(response.data?.body || response.data || []);
      } catch (error) {
        console.warn("Failed to refresh 503A operations:", error.message);
      }
    };

    const handleDummy503aItemCreated = (event) => {
      setTempDummy503aOperation(event.detail.dummyOperation);
    };

    const handle503ACreated = () => {
      setTempDummy503aOperation(null);
      refresh503aOperations();
    };

    const handle503AUpdated = () => {
      refresh503aOperations();
    };

    const handle503ADeleted = (event) => {
      const { deletedOperationUid } = event.detail;
      seta503Operations(prev => prev.filter(op => op.inspection_503a_operation_uid !== deletedOperationUid));
    };

    window.addEventListener('503aDummyItemCreated', handleDummy503aItemCreated);
    window.addEventListener('503aOperationCreated', handle503ACreated);
    window.addEventListener('503aOperationUpdated', handle503AUpdated);
    window.addEventListener('503aOperationDeleted', handle503ADeleted);

    return () => {
      window.removeEventListener('503aDummyItemCreated', handleDummy503aItemCreated);
      window.removeEventListener('503aOperationCreated', handle503ACreated);
      window.removeEventListener('503aOperationUpdated', handle503AUpdated);
      window.removeEventListener('503aOperationDeleted', handle503ADeleted);
    };
  }, [inspectionUid]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        
      // Extract lookupcodetype from pathname instead of using useParams
      const extractLookupCodeType = (pathname) => {
        const match = pathname.match(/\/lookupcodetype\/([^/]+)/);
        return match ? match[1] : null;
      };
      const urlLookupCodeType = extractLookupCodeType(location.pathname);

      const extractCitationLanguageCode = (pathname) => {
        const match = pathname.match(/\/citationLanguageCode\/([^/]+)/);
        return match ? match[1] : null;
      };
      const urlCitationLanguageCode = extractCitationLanguageCode(location.pathname);

        // Base API calls that always need to run
        const baseApiCalls = [
          assignmentApi.get(`/assignments/${assignmentUid}`), 
          ieqipCoreServiceApi.get(`/api/tenants/${tenantId}`)
        ];

        // Conditionally add lookups API call if both URL parameters are available
        if (urlCitationLanguageCode && urlLookupCodeType) {
          baseApiCalls.push(
            ieqipCoreServiceApi.get(`/api/lookups/hierarchy/${urlCitationLanguageCode}/${urlLookupCodeType}`)
          );
        }

        const responses = await Promise.all(baseApiCalls);
        
        const [responseAssignment, responseTenant, responseLookups] = responses;

        setAssignment(responseAssignment.data.body[0]);
        setSubMarkets(responseTenant.data?.sub_markets?.split(',') || []);
        setMarkets(responseTenant.data?.markets?.split(',') || []);
        
        // Load 503B operations separately with error handling
        try {
          // Try the primary endpoint first
          let response503bOperations;
          try {
            response503bOperations = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503boperations`);
          } catch (primaryError) {
            console.warn("Primary 503B endpoint failed, trying alternative format:", primaryError.message);
            // Try alternative endpoint format if the first one fails
            try {
              response503bOperations = await ieqipCoreServiceApi.get(`/api/inspections/list/${inspectionUid}/inspection503boperations`);
            } catch (secondaryError) {
              console.warn("Alternative 503B endpoint also failed:", secondaryError.message);
              throw secondaryError;
            }
          }
          
          setb503Operations(response503bOperations.data?.body || response503bOperations.data || []);
          console.log("503B operations loaded successfully:", (response503bOperations.data?.body || response503bOperations.data || []).length, "operations");
        } catch (error) {
          console.warn("Failed to load 503B operations:", error.message);
          setb503Operations([]);
        }

        // Load 503A operations
        try {
          const response503aOperations = await ieqipCoreServiceApi.get(`/api/inspections/${inspectionUid}/inspection503aoperations`);
          seta503Operations(response503aOperations.data?.body || response503aOperations.data || []);
          console.log("503A operations loaded successfully:", (response503aOperations.data?.body || response503aOperations.data || []).length, "operations");
        } catch (error) {
          console.warn("Failed to load 503A operations:", error.message);
          seta503Operations([]);
        }

        // Only set lookups data if we made the API call
        if (responseLookups) {
          setLookups(responseLookups.data);
        } else {
          setLookups(null);
        }

        // Global lookup data is now handled by useLookupData hook
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    })();
  // eslint-disable-next-line 
  }, [inspectionUid, assignmentUid, idToken, tenantId]);

  const filterRegulations = (subSectionParentUid) => {
    const filteredSubSections = lookup.subSections.filter((subSectionItem) => subSectionItem.parent_uid === subSectionParentUid);

    const filteredRegulations = filteredSubSections.map((filteredSubSectionItem) => {
      const foundRegulation = lookup.regulations.find((regulationItem) => regulationItem.parent_uid === filteredSubSectionItem.lookup_uid);
      return {
        lookup_uid: filteredSubSectionItem.lookup_uid,
        code: filteredSubSectionItem.code,
        description: filteredSubSectionItem.description,
        parent_uid: filteredSubSectionItem.parent_uid,
        description_with_regulation: foundRegulation?.parent_description + " " + foundRegulation?.description,
        regulation_code: foundRegulation?.code,
        regulation_lookup_uid: foundRegulation?.lookup_uid,
      };
    });

    return filteredRegulations;
  };

  const filterCitationLanguages = (citationLanguageParentUid) => {
    return lookup.citationLanguages.filter((citationLanguageItem) => citationLanguageItem.parent_uid === citationLanguageParentUid);
  };

  // Q7
  const filterRegulationsQ7 = (subSectionParentUid) => {
    const filteredSubSections = lookup.subSectionsQ7.filter((subSectionItem) => subSectionItem.parent_uid === subSectionParentUid);

    const filteredRegulations = filteredSubSections.map((filteredSubSectionItem) => {
      const foundRegulation = lookup.regulationsQ7.find((regulationItem) => regulationItem.parent_uid === filteredSubSectionItem.lookup_uid);
      return {
        lookup_uid: filteredSubSectionItem.lookup_uid,
        code: filteredSubSectionItem.code,
        description: filteredSubSectionItem.description,
        parent_uid: filteredSubSectionItem.parent_uid,
        description_with_regulation: foundRegulation?.parent_description + " " + foundRegulation?.description,
        regulation_code: foundRegulation?.code,
        regulation_lookup_uid: foundRegulation?.lookup_uid,
      };
    });
 
    return filteredRegulations;
  };

  const filterCitationLanguagesQ7 = (citationLanguageParentUid) => {
    return lookup.citationLanguagesQ7.filter((citationLanguageItem) => citationLanguageItem.parent_uid === citationLanguageParentUid);
  };

  // Cfr111
  const filterRegulationsCfr111 = (subSectionParentUid) => {
    const filteredSubSections = lookup.subSectionsCfr111.filter((subSectionItem) => subSectionItem.parent_uid === subSectionParentUid);

    const filteredRegulations = filteredSubSections.map((filteredSubSectionItem) => {
      const foundRegulation = lookup.regulationsCfr111.find((regulationItem) => regulationItem.parent_uid === filteredSubSectionItem.lookup_uid);
      return {
        lookup_uid: filteredSubSectionItem.lookup_uid,
        code: filteredSubSectionItem.code,
        description: filteredSubSectionItem.description,
        parent_uid: filteredSubSectionItem.parent_uid,
        description_with_regulation: foundRegulation?.parent_description + " " + foundRegulation?.description,
        regulation_code: foundRegulation?.code,
        regulation_lookup_uid: foundRegulation?.lookup_uid,
      };
    });

    return filteredRegulations;
  };

  const filterCitationLanguagesCfr111 = (citationLanguageParentUid) => {
    return lookup.citationLanguagesCfr111.filter((citationLanguageItem) => citationLanguageItem.parent_uid === citationLanguageParentUid);
  };

  // Cfr820
  const filterRegulationsCfr820 = (subSectionParentUid) => {
    const filteredSubSections = lookup.subSectionsCfr820.filter((subSectionItem) => subSectionItem.parent_uid === subSectionParentUid);

    const filteredRegulations = filteredSubSections.map((filteredSubSectionItem) => {
      const foundRegulation = lookup.regulationsCfr820.find((regulationItem) => regulationItem.parent_uid === filteredSubSectionItem.lookup_uid);
      return {
        lookup_uid: filteredSubSectionItem.lookup_uid,
        code: filteredSubSectionItem.code,
        description: filteredSubSectionItem.description,
        parent_uid: filteredSubSectionItem.parent_uid,
        description_with_regulation: foundRegulation?.parent_description + " " + foundRegulation?.description,
        regulation_code: foundRegulation?.code,
        regulation_lookup_uid: foundRegulation?.lookup_uid,
      };
    });

    return filteredRegulations;
  };

  const filterCitationLanguagesCfr820 = (citationLanguageParentUid) => {
    return lookup.citationLanguagesCfr820.filter((citationLanguageItem) => citationLanguageItem.parent_uid === citationLanguageParentUid);
  };

  // Cfr820211
  const filterRegulationsCfr820211 = (subSectionParentUid) => {
    const filteredSubSections = lookup.subSectionsCfr820211.filter((subSectionItem) => subSectionItem.parent_uid === subSectionParentUid);

    const filteredRegulations = filteredSubSections.map((filteredSubSectionItem) => {
      const foundRegulation = lookup.regulationsCfr820211.find((regulationItem) => regulationItem.parent_uid === filteredSubSectionItem.lookup_uid);
      return {
        lookup_uid: filteredSubSectionItem.lookup_uid,
        code: filteredSubSectionItem.code,
        description: filteredSubSectionItem.description,
        parent_uid: filteredSubSectionItem.parent_uid,
        description_with_regulation: foundRegulation?.parent_description + " " + foundRegulation?.description,
        regulation_code: foundRegulation?.code,
        regulation_lookup_uid: foundRegulation?.lookup_uid,
      };
    });

    return filteredRegulations;
  };

  const filterCitationLanguagesCfr820211 = (citationLanguageParentUid) => {
    return lookup?.citationLanguagesCfr820211.filter((citationLanguageItem) => citationLanguageItem.parent_uid === citationLanguageParentUid);
  };

    // Begin Cfr211820
    const filterRegulationsCfr211820 = (subSectionParentUid) => {
      const filteredSubSections = lookup?.subSectionsCfr211820.filter((subSectionItem) => subSectionItem.parent_uid === subSectionParentUid);
  
      const filteredRegulations = filteredSubSections?.map((filteredSubSectionItem) => {
        const foundRegulation = lookup?.regulationsCfr211820.find((regulationItem) => regulationItem.parent_uid === filteredSubSectionItem.lookup_uid);
        return {
          lookup_uid: filteredSubSectionItem.lookup_uid,
          code: filteredSubSectionItem.code,
          description: filteredSubSectionItem.description,
          parent_uid: filteredSubSectionItem.parent_uid,
          description_with_regulation: foundRegulation?.parent_description + " " + foundRegulation?.description,
          regulation_code: foundRegulation?.code,
          regulation_lookup_uid: foundRegulation?.lookup_uid,
        };
      });
  
      return filteredRegulations;
    };
  
    const filterCitationLanguagesCfr211820 = (citationLanguageParentUid) => {
      return lookup.citationLanguagesCfr211820.filter((citationLanguageItem) => citationLanguageItem.parent_uid === citationLanguageParentUid);
    };
    // End Cfr211820

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.grey[1000]} !important`,
          borderRight: "1px solid",
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-inner-item:hover": {
          color: `${colors.blueAccent[200]} !important`,
        },
        "& .pro-menu-item.active": {
          color: `${colors.blueAccent[500]} !important`,
          backgroundColor: `${colors.blueAccent[100]} !important`,
        },
      }}
    >
      <EqipSideBar>
        {isLoading ? (
          <CircularProgress
            style={{
              margin: "10px 0 20px 100px",
            }}
          />
        ) : (
          <Menu iconShape="square">
            {/* LOGO AND MENU ICON */}
            <MenuItem
              // onClick={() => setIsCollapsed(!isCollapsed)}
              // icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
              style={{
                margin: "10px 0 20px 0",
                color: colors.grey[100],
              }}
            >
                <Box display="flex" justifyContent="space-between">
                  {/* <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                    <MenuOutlinedIcon />
                  </IconButton> */}
                  <Typography variant="h3" color={colors.grey[100]} style={{ marginTop: "0.5em" }}>
                    {assignment?.subject}
                  </Typography>
                </Box>
            </MenuItem>

            {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'DRUG')?.id && <Box>
              <SubMenu
                key="drugs"
                title="DRUGS"
                style={{
                  color: colors.grey[100],
                }}
                defaultOpen={hasSubPartCodeInUrl}
              >
                {/* USFDA */}
                {markets.includes(MARKET.USFDA) && (
                  <SubMenu
                    key="usfda"
                    title="USFDA"
                    style={{
                      color: colors.grey[100],
                    }}
                    defaultOpen={hasSubPartCodeInUrl}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    >
                      <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} />
                    </SubMenu>
                    {subMarkets.includes(SUBMARKET.PAIDRUG) && (
                      <SubMenu
                        key={SUBMARKET.PAIDRUG}
                        title="PAI - Finished Drug"
                        style={{
                          color: colors.grey[100],
                        }}
                      >
                        <Pai paiData={lookup?.paiDrugs} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} />
                      </SubMenu>
                    )}
                    {subMarkets.includes(SUBMARKET.PAIAPI) && (
                      <SubMenu
                        key={SUBMARKET.PAIAPI}
                        title="PAI - API"
                        style={{
                          color: colors.grey[100],
                        }}
                      >
                        <Pai paiData={lookup?.paiApis} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} />
                      </SubMenu>
                    )}
                    {subMarkets.includes(SUBMARKET.CFR211) && (
                      <Practice 
                        lookupcodetype="CFR211" title="21 CFR 211" 
                        sections={lookup?.sections} subPart={subPart} subPartCode={subPartCode} 
                        inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} 
                        filterRegulations={filterRegulations} existRegulationWithNoCitation={existRegulationWithNoCitation} 
                        filterCitationLanguages={filterCitationLanguages} lookups={lookups}/>
                    )}
                    {subMarkets.includes(SUBMARKET.CFR212) && (
                      <SubMenu
                        key="CFR212"
                        title="21 CFR 212"
                        style={{
                          color: colors.grey[100],
                        }}
                      ></SubMenu>
                    )}
                    {subMarkets.includes(SUBMARKET.Q7API) && (
                      <Practice lookupcodetype="Q7API" title="Q7 - API" sections={lookup?.sectionsQ7} subPart={subPartQ7} subPartCode={subPartCodeQ7} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} filterRegulations={filterRegulationsQ7} existRegulationWithNoCitation={existRegulationWithNoCitation} filterCitationLanguages={filterCitationLanguagesQ7} lookups={lookups}/>
                    )}
                    {subMarkets.includes(SUBMARKET.CFR211820) && (
                      <Practice lookupcodetype="CFR211820" title="21 CFR 211820" sections={lookup?.sectionsCfr211820} subPart={subPartCfr211820} subPartCode={subPartCodeCfr211820} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} filterRegulations={filterRegulationsCfr211820} existRegulationWithNoCitation={existRegulationWithNoCitation} filterCitationLanguages={filterCitationLanguagesCfr211820} lookups={lookups}/>
                    )}
                    {subMarkets.includes(SUBMARKET.USFDA503B) && (
                      <Practice503b lookupcodetype="USFDA503B" title="503B Inspections" inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} b503Operations={b503Operations} tempDummyOperation={tempDummyOperation}/>
                    )}
                    {subMarkets.includes(SUBMARKET.USFDA503A) && (
                      <Practice503a lookupcodetype="USFDA503A" title="503A Inspections" inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} a503Operations={a503Operations} tempDummyOperation={tempDummy503aOperation}/>
                    )}
                  </SubMenu>
                )}
                {/* Europe */}
                {markets.includes(MARKET.EU) && (
                  <SubMenu
                    key="eufda"
                    title="EU"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    ></SubMenu>
                  </SubMenu>
                )}
                {/* KOREA */}
                {markets.includes(MARKET.KOREAMFD) && (
                  <SubMenu
                    key="krfda"
                    title="KOREA MFD"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    ></SubMenu>
                  </SubMenu>
                )}
                {/* CANADA */}
                {markets.includes(MARKET.CANADAHEALTH) && (
                  <SubMenu
                    key="canada"
                    title="Health Canada"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    ></SubMenu>
                  </SubMenu>
                )}
              </SubMenu>
            </Box>}
            {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'MEDICAL')?.id && <Box>
              <SubMenu
                key="medicalDevices"
                title="MEDICAL DEVICES"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                {markets.includes(MARKET.USFDA) && (
                  <SubMenu
                    key="usfda"
                    title="USFDA"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    >
                      <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} />   
                    </SubMenu>
                    <Practice lookupcodetype="CFR820" title="21 CFR 820" sections={lookup.sectionsCfr820} subPart={subPartCfr820} subPartCode={subPartCodeCfr820} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} filterRegulations={filterRegulationsCfr820} existRegulationWithNoCitation={existRegulationWithNoCitation} filterCitationLanguages={filterCitationLanguagesCfr820} lookups={lookups}/>
                    {subMarkets.includes(SUBMARKET.CFR820211) && (
                      <Practice lookupcodetype="CFR820211" title="21 CFR 820211" sections={lookup.sectionsCfr820211} subPart={subPartCfr820211} subPartCode={subPartCodeCfr820211} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} filterRegulations={filterRegulationsCfr820211} existRegulationWithNoCitation={existRegulationWithNoCitation} filterCitationLanguages={filterCitationLanguagesCfr820211} lookups={lookups}/>
                    )}
                  </SubMenu>
                )}
                {/* Europe */}
                {markets.includes(MARKET.EU) && (
                  <SubMenu
                    key="eufda"
                    title="EU"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    ></SubMenu>
                  </SubMenu>
                )}
                {/* KOREA */}
                {markets.includes(MARKET.KOREAMFD) && (
                  <SubMenu
                    key="krfda"
                    title="KOREA MFD"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    ></SubMenu>
                  </SubMenu>
                )}
                {/* CANADA */}
                {markets.includes(MARKET.CANADAHEALTH) && (
                  <SubMenu
                    key="canada"
                    title="Health Canada"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <SubMenu
                      key="general"
                      title="General Information"
                      style={{
                        color: colors.grey[100],
                      }}
                    ></SubMenu>
                  </SubMenu>
                )}
              </SubMenu>
            </Box>}
            <Box>
              {/* <Box style={{
                  color: colors.grey[100],
                  backgroundColor: colors.blueAccent[100],
                }}>
                <Typography variant="h5" color={colors.grey[100]} sx={{ m: "5px 0 0 10px" }}>
                  FOOD
                </Typography>
              </Box> */}
              {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'FNB')?.id && <SubMenu
                key="food"
                title="FOOD"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                <SubMenu
                  key="usfda"
                  title="USFDA"
                  style={{
                    color: colors.grey[100],
                  }}
                >
                  <SubMenu
                    key="general"
                    title="General Information"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} />
                  </SubMenu>
                  <Practice lookupcodetype="CFR111" title="21 CFR 111" sections={lookup.sectionsCfr111} subPart={subPartCfr111} subPartCode={subPartCodeCfr111} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} filterRegulations={filterRegulationsCfr111} existRegulationWithNoCitation={existRegulationWithNoCitation} filterCitationLanguages={filterCitationLanguagesCfr111} lookups={lookups}/>
                </SubMenu>
              </SubMenu>}
              {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'BIO')?.id && <SubMenu
                key="biologis"
                title="BIOLOGICS"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                <SubMenu
                  key="usfda"
                  title="USFDA"
                  style={{
                    color: colors.grey[100],
                  }}
                >
                  <SubMenu
                    key="general"
                    title="General Information"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    {/* <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} /> */}
                  </SubMenu>
                </SubMenu>
              </SubMenu>}
            </Box>
            <Box>
              {/* <Box style={{
                  color: colors.grey[100],
                  backgroundColor: colors.blueAccent[100],
                }}>
                <Typography variant="h5" color={colors.grey[100]} sx={{ m: "5px 0 0 10px" }}>
                  FOOD
                </Typography>
              </Box> */}
              {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'COMPROD')?.id && <SubMenu
                key="comprod"
                title="COMBO PRODUCTS"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                <SubMenu
                  key="usfda"
                  title="USFDA"
                  style={{
                    color: colors.grey[100],
                  }}
                >
                  <SubMenu
                    key="general"
                    title="General Information"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    {/* <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} /> */}
                  </SubMenu>
                  {/* <Practice lookupcodetype="CFR111" title="CFR 111" sections={sectionsCfr111} subPart={subPartCfr111} subPartCode={subPartCodeCfr111} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} colors={colors} filterRegulations={filterRegulationsCfr111} existRegulationWithNoCitation={existRegulationWithNoCitation} filterCitationLanguages={filterCitationLanguagesCfr111} lookups={lookups}/> */}
                </SubMenu>
              </SubMenu> }
              {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'DISSUP')?.id && <SubMenu
                key="DISSUP"
                title="DIETARY SUPPLEMENTS"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                <SubMenu
                  key="usfda"
                  title="USFDA"
                  style={{
                    color: colors.grey[100],
                  }}
                >
                  <SubMenu
                    key="general"
                    title="General Information"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    {/* <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} /> */}
                  </SubMenu>
                </SubMenu>
              </SubMenu>}
              {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'COS')?.id && <SubMenu
                key="cos"
                title="COSMETICS"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                <SubMenu
                  key="usfda"
                  title="USFDA"
                  style={{
                    color: colors.grey[100],
                  }}
                >
                  <SubMenu
                    key="general"
                    title="General Information"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    {/* <General generals={lookup?.generals} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} /> */}
                  </SubMenu>
                </SubMenu>
              </SubMenu>}
              {assignment?.commodity_id === COMMODITY_TENANT_LIST.find((commodity) => commodity.code === 'BIMO')?.id && <SubMenu
                key="bimo"
                title="BIMO"
                style={{
                  color: colors.grey[100],
                }}
              >
                {/* USFDA */}
                <SubMenu
                  key="usfda"
                  title="USFDA"
                  style={{
                    color: colors.grey[100],
                  }}
                >
                  <SubMenu
                    key="general"
                    title="General Information"
                    style={{
                      color: colors.grey[100],
                    }}
                  >
                    <General generals={lookup?.generalsBimo} inspectionUid={inspectionUid} selected={selected} setSelected={setSelected} />
                  </SubMenu>
                </SubMenu>
              </SubMenu>}
            </Box>
            <Box>
              {/* <Box style={{
                  color: colors.grey[100],
                  backgroundColor: colors.blueAccent[100],
                }}>
                <Typography variant="h5" color={colors.grey[100]} sx={{ m: "5px 0 0 10px" }}>
                  REPORTS
                </Typography>
              </Box> */}
              <SubMenu
                key="reports"
                title="REPORTS"
                style={{
                  color: colors.grey[100],
                }}
              >
                <Item title="Inspection" to={`/inspection/${inspectionUid}/inspectionreport`} selected={selected} setSelected={setSelected} />
                <Item title="Observation" to={`/inspection/${inspectionUid}/observationordering`} selected={selected} setSelected={setSelected} />
                {/* <Item title="Observation Old" to={`/inspection/${inspectionUid}/citationreport`} selected={selected} setSelected={setSelected} /> */}
                {/* <Item title="Discussion Old" to={`/inspection/${inspectionUid}/discussionreport`} selected={selected} setSelected={setSelected} /> */}
                <Item title="Discussion" to={`/inspection/${inspectionUid}/discussionordering`} selected={selected} setSelected={setSelected} />
              </SubMenu>
            </Box>
          </Menu>
        )}
      </EqipSideBar>
    </Box>
  );
};

export default Sidebar;
